import React, { useMemo } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LogFormShell from '../UI/LogFormShell';
import colors from '../../assets/theme/colors';
import MOOD_OPTIONS, { getMoodOption } from '../../constants/moodOptions';

const LogMoodModal = ({ open, onClose, child, currentMood, onSave, onClearMood }) => {
  const activeMoodOption = useMemo(() => (
    currentMood ? getMoodOption(currentMood) : null
  ), [currentMood]);
  const [savingMoodKey, setSavingMoodKey] = React.useState(null);

  const handleSelectMood = async (mood) => {
    if (!mood?.label || savingMoodKey) {
      return;
    }

    setSavingMoodKey(mood.key);

    try {
      await onSave?.(mood.label);
      onClose?.();
    } finally {
      setSavingMoodKey(null);
    }
  };

  const handleClear = async () => {
    if (onClearMood) {
      await onClearMood();
    }
    onClose?.();
  };

  const childFirstName = String(child?.name || 'this child')
    .trim()
    .split(/\s+/)[0] || 'this child';
  const hasCurrentMood = Boolean(activeMoodOption);
  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={`Today's mood for ${childFirstName}`}
      subtitle="Select one to update today's mood."
      titleWeight={700}
      titleSx={{
        fontSize: '20px',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 'calc(100% - 48px)',
      }}
      subtitleSx={{
        mt: 0.85,
        mb: 2.25,
        fontSize: '14px',
        lineHeight: 1.4,
        color: colors.semantic.ink2,
      }}
      closeButtonSx={{
        width: 32,
        height: 32,
        bgcolor: colors.semantic.sageMist,
        borderColor: colors.semantic.sageMist,
        '&:hover': {
          bgcolor: colors.semantic.sageMist,
        },
      }}
      closeIconSx={{
        color: colors.semantic.ink,
        fontSize: 18,
      }}
      forceDrawer
      maxWidth="sm"
      surfaceSx={{
        borderTopLeftRadius: 26,
        borderTopRightRadius: 26,
        height: 'min(60dvh, 430px)',
        maxHeight: 'min(60dvh, 430px)',
      }}
      bodySx={{
        px: { xs: 1.25, sm: 2 },
        pt: 0.2,
        pb: 1,
      }}
      footer={(
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch', pb: 0.4 }}>
          {hasCurrentMood ? (
            <Button
              variant="outlined"
              onClick={handleClear}
              sx={{
                flex: 1,
                height: 50,
                borderRadius: '999px',
                borderWidth: '1.5px',
                borderColor: colors.semantic.line,
                bgcolor: colors.semantic.bg,
                color: colors.semantic.ink,
                fontSize: '15px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: colors.semantic.bg,
                  borderColor: colors.semantic.line2,
                  boxShadow: 'none',
                },
              }}
            >
              Clear mood
            </Button>
          ) : null}
          <Box
            sx={{
              flex: hasCurrentMood ? 2 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 50,
              px: 2,
              borderRadius: '999px',
              bgcolor: colors.semantic.bg,
              border: `1px dashed ${colors.semantic.line}`,
              color: colors.semantic.ink2,
              fontSize: '15px',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            Tap a mood to update it
          </Box>
        </Box>
      )}
    >
      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 0.55,
          mt: 0.4,
        }}
      >
        {MOOD_OPTIONS.map((mood) => {
          const active = activeMoodOption?.key === mood.key;
          return (
            <ButtonBase
              key={mood.key}
              onClick={() => handleSelectMood(mood)}
              disabled={Boolean(savingMoodKey)}
              aria-pressed={active}
              sx={{
                minHeight: { xs: 104, sm: 108 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.35,
                px: '4px',
                py: '14px',
                pb: '12px',
                borderRadius: '16px',
                border: `2px solid ${active ? mood.tint : colors.semantic.line}`,
                bgcolor: active ? alpha(mood.tint, 0.22) : colors.semantic.bg,
                boxShadow: 'none',
                transform: active ? 'scale(1.03)' : 'scale(1)',
                transition: 'all 180ms ease',
                '&:active': {
                  transform: active ? 'scale(1.01)' : 'scale(0.99)',
                },
                '&.Mui-disabled': {
                  opacity: 0.7,
                },
                '&:focus-visible': {
                  outline: `2px solid ${mood.color}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Box sx={{ fontSize: 32, lineHeight: 1, color: active ? mood.color : mood.tint, display: 'grid', placeItems: 'center', mb: 0 }}>
                {mood.iconComponent ? <mood.iconComponent sx={{ fontSize: 32 }} /> : null}
              </Box>
              <Typography sx={{ fontSize: '11.5px', fontWeight: active ? 650 : 500, color: active ? mood.color : colors.semantic.ink, lineHeight: 1.02 }}>
                {mood.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Stack>
    </LogFormShell>
  );
};

export default LogMoodModal;
